import { Cart, CartItem } from "./CartModel";
import { initBfWaveAnimation, playBfWaveAnimations } from "../bfWaveAnimation";
import { calculateShippingCost, mapCountryToGroup, SHIPPING_RATES } from "../shippingRates";
import { calculateAllocatedBundleSavings } from "../ekBundleMath.js";

/**
 * Global reference to the openCartDrawer function that allows external access
 * This reference is set during initialization and used by the exported openCartDrawerGlobal function
 * to allow other components to open the cart drawer without direct access to internal functions
 */
let globalOpenCartDrawer: ((event?: Event) => void) | null = null;

// Global state for BF setting
let isBfEnabled = false;

/**
 * Initialize cart drawer with settings
 */
export function initCartDrawer(settings: { isBf: boolean }) {
  isBfEnabled = settings.isBf;
}

// Active presentation context
let activeCurrency: string = "EUR";

function getPriceLocale(): string {
  const shopify = (window as any)?.Shopify;
  if (shopify?.locale && shopify?.country) return `${shopify.locale}-${shopify.country}`;
  return "de-DE";
}
let latestMarketCountry: string = ((window as any)?.Shopify?.country || "").toUpperCase();

// Utility
const emit = (name: string, detail: any) => {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch (e) {}
};

// Track latest market announcements globally
window.addEventListener("ek:market-ready", (e: any) => {
  const country = (e && e.detail && e.detail.country) || latestMarketCountry;
  latestMarketCountry = (country || latestMarketCountry || "").toUpperCase();
});
window.addEventListener("ek:market-change", (e: any) => {
  const country = e && e.detail && e.detail.country;
  if (country) latestMarketCountry = (country || "").toUpperCase();
});

function getDetectedCountry(): string {
  const detected = latestMarketCountry || ((window as any)?.Shopify?.country || "").toUpperCase();
  const group = mapCountryToGroup(detected);
  if (SHIPPING_RATES[group]) return group;
  return "DE";
}
/**
 * DOM element references used throughout the cart drawer functionality
 * These are cached at initialization to avoid repeated DOM queries
 */
const cartOverlay = document.getElementById("cartOverlay");
const cartContents = document.getElementById("cartContents");
const cartFooter = document.getElementById("cartFooter");
const cartHeader = document.getElementById("cartHeader");

/**
 * Track the current state of the cart drawer to prevent animation conflicts
 * Possible states: 'closed', 'opening', 'open', 'closing'
 */
let drawerState: "closed" | "opening" | "open" | "closing" = "closed";

/**
 * Track if we need to reopen after closing completes
 */
let pendingReopen = false;

/**
 * Skip the overlay fade when the cart drawer is opened from the upsell drawer
 * so the dimmed backdrop stays put while the cart panel slides in.
 */
let openFromUpsell = false;
const CART_OPENED_FROM_UPSELL = "ek:cart-opened-from-upsell";

/**
 * Opens the cart drawer with a smooth animation
 *
 * This function handles:
 * 1. Making the overlay visible and clickable
 * 2. Animating the cart container into view (differently for mobile vs desktop)
 * 3. Preventing page scrolling while the drawer is open
 * 4. Fetching the latest cart items to display
 *
 * @param event - Optional event object that will be prevented if provided
 */
function openCartDrawer(event?: Event) {
  if (event) event.preventDefault();

  // If currently closing, mark for reopen after close completes
  if (drawerState === "closing") {
    pendingReopen = true;
    return;
  }

  // If already opening, skip to avoid interrupting animation
  if (drawerState === "opening") {
    return;
  }

  // If already open, just refresh the cart contents
  if (drawerState === "open") {
    fetchCartItems();
    return;
  }

  // Mark as opening to prevent duplicate calls
  drawerState = "opening";
  const fromUpsell = openFromUpsell;
  openFromUpsell = false;

  if (cartOverlay) {
    cartOverlay.classList.remove("pointer-events-none");
    cartOverlay.classList.remove("hidden");
    if (fromUpsell) {
      cartOverlay.classList.remove("bg-black/0");
      cartOverlay.classList.add("bg-black/30");
    }

    // Then animate the container
    const cartContainer = document.getElementById("cartContainer");
    if (cartContainer) {
      // Use requestAnimationFrame to ensure the browser has painted the initial state
      // before we trigger the animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          cartOverlay.classList.remove("bg-black/0");
          cartOverlay.classList.add("bg-black/30");
          // Different animation based on screen size (already set in CSS with md: classes)
          cartContainer.classList.remove("translate-y-full", "md:translate-x-full");

          // playBfWaveAnimations(cartOverlay);

          // Mark as fully open after animation completes
          setTimeout(() => {
            drawerState = "open";
            if (fromUpsell) window.dispatchEvent(new Event(CART_OPENED_FROM_UPSELL));
          }, 300);
        });
      });
    }

    // Prevent body scrolling
    document.body.style.overflow = "hidden";

    fetchCartItems();

    // Check shadows after opening the drawer
    setTimeout(updateCartShadows, 300);
  }
}

/**
 * Closes the cart drawer with a smooth animation
 *
 * This function handles:
 * 1. Animating the cart container out of view (differently for mobile vs desktop)
 * 2. Hiding the overlay after the animation completes
 * 3. Re-enabling page scrolling
 * 4. Using setTimeout to ensure proper animation sequencing
 */
function closeCartDrawer() {
  // Prevent closing if already closed or closing
  if (drawerState === "closed" || drawerState === "closing") {
    return;
  }

  // Mark as closing to prevent duplicate calls
  drawerState = "closing";

  if (cartOverlay) {
    // First animate the container
    const cartContainer = document.getElementById("cartContainer");
    if (cartContainer) {
      // Different animation based on screen size (already set in CSS with md: classes)
      cartContainer.classList.add("translate-y-full", "md:translate-x-full");
    }

    // Then after animation completes, hide the overlay
    cartOverlay.classList.remove("bg-black/30");
    cartOverlay.classList.add("bg-black/0");
    setTimeout(() => {
      cartOverlay.classList.add("hidden");
      cartOverlay.classList.add("pointer-events-none");
      // ponytail: upsell handoff leaves html overflow/overscroll locked; cart only owned body overflow
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.documentElement.style.overscrollBehavior = "";
      document.body.style.overscrollBehavior = "";

      // Mark as fully closed
      drawerState = "closed";

      // Clean up the timeout to prevent memory leaks
      if (updateCartShadows.timeout) {
        clearTimeout(updateCartShadows.timeout);
        updateCartShadows.timeout = null;
      }

      // If there's a pending reopen, trigger it now
      if (pendingReopen) {
        pendingReopen = false;
        openCartDrawer();
      }
    }, 300);
  }
}

// Set global reference to the openCartDrawer function so it can be accessed externally
globalOpenCartDrawer = openCartDrawer;

// Track if event listeners are attached to avoid duplicates
let scrollListenerAttached = false;
let shippingListenersAttached = false;

// Debounce timer for shipping updates
let shippingUpdateDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const CART_ITEM_REMOVE_ANIMATION_DURATION_MS = 280;
const pendingRemovalAnimationTimeouts = new Set<number>();

function cancelPendingOptimisticRemovalAnimations() {
  for (const timeoutId of pendingRemovalAnimationTimeouts) {
    window.clearTimeout(timeoutId);
  }
  pendingRemovalAnimationTimeouts.clear();
}

/**
 * Cache for variant compare_at_prices to avoid repeated API calls
 * Maps variant_id to compare_at_price (in cents, or null if no compare_at_price)
 */
const variantCompareAtPriceCache: Map<number, number | null> = new Map();

/** Product handles whose `/products/{handle}.js` fetch failed (404/403) — skip repeat requests. */
const failedProductHandleCache = new Set<string>();

/** Last rendered cart state for layout-only refresh (e.g. resize) without re-fetching product JSON. */
let lastCartSnapshot: Cart | null = null;

/**
 * Fetches compare_at_price for all variants in the cart from the Product API
 * The Cart API doesn't include variant.compare_at_price, so we need to fetch it separately
 *
 * @param cart - The cart data containing items with product handles
 * @returns A Map of variant_id to compare_at_price (in cents)
 */
async function fetchVariantCompareAtPrices(cart: Cart): Promise<Map<number, number | null>> {
  const compareAtPrices: Map<number, number | null> = new Map();

  // Get unique product handles that have variants not yet cached
  const uncachedHandles = new Set<string>();

  for (const item of cart.items) {
    if (!variantCompareAtPriceCache.has(item.variant_id)) {
      if (failedProductHandleCache.has(item.handle)) {
        variantCompareAtPriceCache.set(item.variant_id, null);
        compareAtPrices.set(item.variant_id, null);
        continue;
      }
      uncachedHandles.add(item.handle);
    } else {
      compareAtPrices.set(item.variant_id, variantCompareAtPriceCache.get(item.variant_id) ?? null);
    }
  }

  if (uncachedHandles.size === 0) return compareAtPrices;

  // Fetch product data for uncached handles in parallel
  const fetchPromises = Array.from(uncachedHandles).map(async (handle) => {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        failedProductHandleCache.add(handle);
        for (const item of cart.items) {
          if (item.handle === handle) {
            variantCompareAtPriceCache.set(item.variant_id, null);
          }
        }
        return null;
      }
      const product = await response.json();
      return product;
    } catch {
      failedProductHandleCache.add(handle);
      for (const item of cart.items) {
        if (item.handle === handle) {
          variantCompareAtPriceCache.set(item.variant_id, null);
        }
      }
      return null;
    }
  });

  const results = await Promise.all(fetchPromises);

  // Build variant -> compare_at_price map from fetched products
  for (const product of results) {
    if (!product || !product.variants) continue;
    for (const variant of product.variants) {
      const compareAt = variant.compare_at_price ?? null;
      variantCompareAtPriceCache.set(variant.id, compareAt);
      compareAtPrices.set(variant.id, compareAt);
    }
  }

  // Add any remaining cached values for items in the cart
  for (const item of cart.items) {
    if (!compareAtPrices.has(item.variant_id) && variantCompareAtPriceCache.has(item.variant_id)) {
      compareAtPrices.set(item.variant_id, variantCompareAtPriceCache.get(item.variant_id) ?? null);
    }
  }

  return compareAtPrices;
}

/**
 * Fetches the current cart data from the Shopify Cart API
 *
 * This function:
 * 1. Makes an AJAX request to the Shopify Cart API
 * 2. Fetches compare_at_prices for variants (not included in Cart API)
 * 3. Updates the cart contents with the retrieved data
 * 4. Updates the cart count in the UI
 * 5. Calculates and displays the total price
 * 6. Handles any errors that might occur during the fetch
 */
async function fetchCartItems() {
  try {
    const response = await fetch("/cart.js");
    const cart: Cart = await response.json();

    // Update active currency from cart data
    activeCurrency = (cart as any)?.currency || activeCurrency;

    // Fetch compare_at_prices from Product API (Cart API doesn't include this)
    const compareAtPrices = await fetchVariantCompareAtPrices(cart);

    lastCartSnapshot = cart;

    updateCartContents(cart, compareAtPrices);
    updateCartCount(cart.item_count);
    updateCartShadows();
    calculateTotalPrice(cart);
  } catch (error) {
    console.error("Error fetching cart:", error);
  }
}

/**
 * Helper function to format prices in the German/European format (e.g., 10,99 €)
 * Uses the browser's Intl.NumberFormat API for proper localization
 *
 * @param price - The price to format in decimal form (e.g., 10.99)
 * @returns A formatted price string (e.g., "10,99 €")
 */
function formatPrice(price: number) {
  const currency = activeCurrency || "EUR";
  return new Intl.NumberFormat(getPriceLocale(), {
    style: "currency",
    currency,
  }).format(price);
}

/**
 * Updates the shipping cost display based on the selected country
 * Fetches the current cart, calculates shipping based on weight and country,
 * then updates the display and saves the selected country to localStorage
 *
 * @param countryCode - The selected country code (e.g., "DE", "AT", "EU")
 * @param cartData - Optional cart data to avoid redundant fetch
 */
async function updateShippingCountry(countryCode: string, existingCartData?: Cart) {
  try {
    let cartData: Cart;
    if (existingCartData) {
      cartData = existingCartData;
    } else {
      const cartResponse = await fetch("/cart.js");
      cartData = await cartResponse.json();
    }
    activeCurrency = (cartData as any)?.currency || activeCurrency;
    const weightInKg = cartData.total_weight / 1000;
    const shippingCost = calculateShippingCost(countryCode, weightInKg);

    const shippingSection = document.getElementById("cartShippingSection");
    if (!shippingSection) return;

    const shippingPriceElement = shippingSection.querySelector("#shippingPrice") as HTMLElement | null;
    const subtotalRow = shippingSection.querySelector("#subtotalRow") as HTMLElement | null;
    const subtotalPrice = shippingSection.querySelector("#subtotalPrice") as HTMLElement | null;

    if (shippingPriceElement) {
      if (shippingCost === 0) {
        shippingPriceElement.textContent = "Versandkostenfrei";
        shippingPriceElement.className = "text-black/30 text-base tracking-[0.5px] freeShipping";
      } else {
        shippingPriceElement.textContent = formatPrice(shippingCost);
        shippingPriceElement.className = "text-secondary text-base tracking-[0.5px]";
      }

      if (subtotalRow && subtotalPrice) {
        if (shippingCost === 0) {
          subtotalRow.style.display = "none";
        } else {
          subtotalRow.style.display = "flex";
          subtotalPrice.textContent = formatPrice(cartData.total_price / 100);
        }
      }
    }

    calculateTotalPrice(cartData);
  } catch (error) {
    console.error("Error updating shipping:", error);
  }
}

/**
 * Calculates and displays the total price including shipping
 *
 * This function:
 * 1. Uses the provided cart data or fetches it if not provided
 * 2. Extracts the cart subtotal and converts from cents to euros
 * 3. Calculates shipping from cart weight and selected country
 * 4. Calculates the final total (subtotal + shipping)
 * 5. Updates the price displays in the UI
 *
 * @param cartData - Optional cart data object to use for calculations
 */
async function calculateTotalPrice(cartData?: Cart) {
  try {
    if (!cartData) {
      const cartResponse = await fetch("/cart.js");
      cartData = await cartResponse.json();
    }

    if (!cartData) return;
    activeCurrency = (cartData as any)?.currency || activeCurrency;

    const shippingSection = document.getElementById("cartShippingSection");
    if (!shippingSection) return;

    const cartFinalPrice = cartData.total_price / 100;
    const totalCartPrice = shippingSection.querySelector("#totalCartPrice") as HTMLElement | null;
    const subtotalRow = shippingSection.querySelector("#subtotalRow") as HTMLElement | null;
    const subtotalPrice = shippingSection.querySelector("#subtotalPrice") as HTMLElement | null;
    const shippingPriceElement = shippingSection.querySelector("#shippingPrice") as HTMLElement | null;

    if (!totalCartPrice) return;

    // Calculate shipping cost numerically from cart weight and selected country
    // instead of parsing formatted text from the DOM (which breaks with locale-dependent formatting)
    const shippingSelect = shippingSection.querySelector("#shippingSelect") as HTMLSelectElement | null;
    const selectedCountry = shippingSelect?.value || getDetectedCountry();
    const weightInKg = cartData.total_weight / 1000;
    const shippingCost = calculateShippingCost(selectedCountry, weightInKg);

    if (shippingCost > 0) {
      if (shippingPriceElement) {
        shippingPriceElement.className = "text-secondary text-base tracking-[0.5px]";
      }

      if (subtotalRow && subtotalPrice) {
        subtotalRow.style.display = "flex";
        subtotalPrice.textContent = formatPrice(cartFinalPrice);
      }
    } else {
      if (shippingPriceElement) {
        shippingPriceElement.className = "text-black/30 text-base tracking-[0.5px] freeShipping";
      }

      if (subtotalRow) {
        subtotalRow.style.display = "none";
      }
    }

    const finalTotal = cartFinalPrice + shippingCost;
    totalCartPrice.textContent = formatPrice(finalTotal);
  } catch (error) {
    console.error("Error calculating total price:", error);
  }
}

/**
 * Updates the cart contents display with the latest cart data
 *
 * This function:
 * 1. Creates a container for cart items
 * 2. Generates HTML for each cart item or shows an empty cart message
 * 3. Inserts the container at the beginning of the cart contents
 * 4. Updates the shipping section
 * 5. Attaches event listeners to the quantity adjustment buttons
 *
 * The implementation ensures that the cart items appear before any other content
 * in the cart drawer, preserving rendered templates like the shipping section.
 *
 * @param cart - The cart data object containing items and other information
 * @param compareAtPrices - Map of variant_id to compare_at_price (fetched from Product API)
 */
function updateCartContents(cart: Cart, compareAtPrices: Map<number, number | null> = new Map()) {
  if (!cartContents) return;
  activeCurrency = (cart as any)?.currency || activeCurrency;
  // A fresh render replaces rows, so cancel stale optimistic-removal callbacks.
  cancelPendingOptimisticRemovalAnimations();

  // Create a container for cart items
  const cartItemsContainer = document.createElement("div");
  cartItemsContainer.id = "cartItemsContainer";

  if (cart.item_count === 0) {
    cartItemsContainer.innerHTML = `<div class="flex flex-col items-center justify-center gap-4 h-[60vh] pb-[10rem] md:pb-0">
        <img src="https://cdn.shopify.com/s/files/1/0689/3587/6929/files/Warenkorb_sleep.gif?v=1751025508" alt="Schlafender Warenkorb" class="mb-4 max-h-[5rem]" loading="lazy" />
        <p class="">Dein Warenkorb ist noch leer</p>
        <a href="/collections/shop" class="button-black">Entdecke unsere Produkte</a>
        </div>`;
    if (cartFooter) cartFooter.classList.add("hidden");
    // Remove shipping section if present
    const shippingSection = document.getElementById("cartShippingSection");
    if (shippingSection) shippingSection.remove();
  } else {
    let cartHTML = "";
    const bundleSavings = calculateAllocatedBundleSavings(cart.items);

    // Initialize shadows
    updateCartShadows();

    // Add cart items
    cart.items
      .filter((item: CartItem) => item.quantity > 0)
      .forEach((item: CartItem) => {
        const itemImage = item.image ? item.image : item.featured_image?.url || "";
        const finalLinePrice = (typeof item.final_line_price === "number" && item.final_line_price > 0 ? item.final_line_price : item.line_price) / 100;
        const isBundleItem = Boolean(item.properties?._ek_bundle);

        // Calculate highest price (matching customer-cart.liquid logic)
        let highestUnitPrice = finalLinePrice / item.quantity;

        // Check compare_at_price from Product API (Cart API doesn't include this)
        const variantCompareAtPrice = compareAtPrices.get(item.variant_id);
        if (!isBundleItem && variantCompareAtPrice && variantCompareAtPrice > highestUnitPrice * 100) {
          highestUnitPrice = variantCompareAtPrice / 100;
        }

        // Set-bonus template test: the PDP stores the component sum (cents) on the line
        const setSum = Number(item.properties?._set_sum);
        if (setSum > highestUnitPrice * 100) {
          highestUnitPrice = setSum / 100;
        }

        // Check original_line_price
        const originalUnitPrice = item.original_line_price ? item.original_line_price / 100 / item.quantity : 0;
        if (originalUnitPrice > highestUnitPrice) {
          highestUnitPrice = originalUnitPrice;
        }

        const highestTotal = highestUnitPrice * item.quantity;

        // Determine discount percentage for badge
        let discountPercentage = 0;
        if (item.original_line_price && item.original_line_price > item.final_line_price) {
          discountPercentage = Math.round(((item.original_line_price - item.final_line_price) / item.original_line_price) * 100);
        }

        // Build price display with compare-at price strikethrough
        let displayPrice = "";
        if (highestTotal !== finalLinePrice) {
          const formattedHighestPrice = formatPrice(highestTotal);
          const formattedFinalPrice = formatPrice(finalLinePrice);

          displayPrice = `
              <div class="shrink-0 text-base price flex flex-col items-end">
                <div class="pb-1 text-right line-through text-secondary">${formattedHighestPrice}</div>
                <div class="mb-1 w-20 text-right">${formattedFinalPrice}</div>
              </div>
            `;
        } else {
          const formattedPrice = formatPrice(finalLinePrice);
          displayPrice = `<div class="text-base">${formattedPrice}</div>`;
        }

        let badgeHtml = "";
        if (discountPercentage > 0) {
          let badgeColor = "#EEDFD2";
          let textColor = "text-[#614e3d]";

          if (discountPercentage === 50) {
            badgeColor = "#EEDFD2";
          } else if (discountPercentage === 30 || discountPercentage === 20) {
            badgeColor = "#EEDFD2";
          } else if ([10, 12, 13, 7, 2].includes(discountPercentage)) {
            badgeColor = "#EEDFD2";
            textColor = "text-[#614e3d]";
          }

          // badgeHtml = `
          //   <div class="flex items-center justify-center rounded-full w-[38px] h-[38px]" style="background-color: ${badgeColor};">
          //     <span class="font-display ${textColor} text-[12px] md:text-[13px] font-semibold leading-none">${discountPercentage}%</span>
          //   </div>
          // `;
        }

        const decreaseButtonClass =
          item.quantity === 1
            ? "text-xl m-1 size-6 leading-[1.5rem] font-medium rounded-full cart-item-decrease disabled text-black/30 transition-colors duration-150"
            : "text-xl m-1 size-6 leading-[1.5rem] font-medium rounded-full cart-item-decrease transition-colors duration-150";

        // Match Shopify checkout: product title + variant title (no combined-listing logic)
        const title = item.product_title;
        const subtitleValues = item.variant_options.filter((o) => o !== "Default Title" && !title.includes(o));
        const subtitle = subtitleValues?.length ? subtitleValues.join(" | ") : "";

        cartHTML += `
          <div class="cart-item-row flex gap-3 py-5 border-b" data-cart-item-id="${item.id}">
          <a href="${item.url}" class="flex-shrink-0">
            <img src="${itemImage}" alt="${item.title}" class="w-[83px] h-[100px] object-contain">
          </a>
            <div class="flex-grow flex flex-col">
              <div class="flex justify-between items-start">
              <a href="${item.url}">
                <div class="font-semibold text-base md:max-w-[212px]">${title}</div>
              </a>
                <button class="text-black/30 text-sm active:text-berry hover:text-berry cart-item-remove" data-id="${item.id}">Entfernen</button>
              </div>
              ${subtitle ? `<div class="text-sm black max-w-[196px]">${subtitle}</div>` : ""}
              <div class="flex justify-between items-end mb-0 mt-auto">
                <div class="flex items-center gap-5">
                  <div class="flex items-center border rounded-full mt-2">
                    <button class="${decreaseButtonClass} pb-[3px]  leading-none" data-id="${item.id}" ${item.quantity === 1 ? "disabled" : ""}>-</button>
                    <span class="appearance-none bg-transparent border-none p-0 font-medium text-center size-6  border-lightgray">${item.quantity}</span>
                    <button class="text-xl pb-[3px] m-1 size-6 leading-none font-medium rounded-full cart-item-increase transition-colors duration-150 flex items-center justify-center" data-id="${item.id}">+</button>
                  </div>
                  
                </div>
                ${badgeHtml}
                ${displayPrice}
              </div>
            </div>
          </div>
        `;
      });

    if (bundleSavings > 0) {
      cartHTML += `
        <div class="flex items-center justify-between border-b py-4 text-sm">
          <span class="uppercase tracking-[0.6px]">Set-Vorteil</span>
          <strong class="font-medium text-berry">−${formatPrice(bundleSavings / 100)}</strong>
        </div>
      `;
    }

    cartItemsContainer.innerHTML = cartHTML;
    if (cartFooter) cartFooter.classList.remove("hidden");
    // Create or update the shipping section
    updateShippingSection(cart);
  }

  // Remove any previous cartItemsContainer if it exists
  const existingContainer = document.getElementById("cartItemsContainer");
  if (existingContainer) {
    existingContainer.remove();
  }

  // Insert the cart items at the beginning of cartContents
  if (cartContents.firstChild) {
    cartContents.insertBefore(cartItemsContainer, cartContents.firstChild);
  } else {
    cartContents.appendChild(cartItemsContainer);
  }

  // Add scroll event listener for shadow effects if not already attached
  if (!scrollListenerAttached) {
    cartContents.addEventListener("scroll", updateCartShadows, { passive: true });
    scrollListenerAttached = true;
  }

  // Add event listeners for quantity buttons
  cartItemsContainer.querySelectorAll(".cart-item-decrease").forEach((button) => {
    button.addEventListener("click", (event) => {
      // Skip if button is disabled
      if (button.classList.contains("disabled")) return;
      // Add tap animation
      addTapAnimation(button, event);
      const itemId = (button as HTMLElement).dataset.id;
      updateItemQuantity(itemId, "decrease");
    });
  });

  cartItemsContainer.querySelectorAll(".cart-item-increase").forEach((button) => {
    button.addEventListener("click", (event) => {
      // Add tap animation
      addTapAnimation(button, event);
      const itemId = (button as HTMLElement).dataset.id;
      updateItemQuantity(itemId, "increase");
    });
  });

  cartItemsContainer.querySelectorAll(".cart-item-remove").forEach((button) => {
    button.addEventListener("click", () => {
      const removeButton = button as HTMLButtonElement;
      if (removeButton.dataset.removing === "true") return;

      const itemId = removeButton.dataset.id;
      if (!itemId) return;

      removeButton.dataset.removing = "true";
      removeButton.disabled = true;
      const cartItemRow = cartItemsContainer.querySelector(`.cart-item-row[data-cart-item-id="${itemId}"]`) as HTMLElement | null;
      animateCartItemOptimisticRemoval(cartItemRow);
      updateItemQuantity(itemId, "remove");
    });
  });
}

/**
 * Creates or updates the shipping section in the cart drawer
 *
 * This function:
 * 1. Creates the HTML structure for shipping selection and total
 * 2. Adds the section to the cart drawer if it doesn't exist
 * 3. Sets up event listeners for the shipping country select
 * 4. Initializes the shipping country from localStorage
 * 5. Updates the total price display
 *
 * @param cart - The cart data object to use for the shipping section
 */

function updateShippingSection(cart: Cart) {
  // Create or get the shipping section container
  let shippingSection = document.getElementById("cartShippingSection");
  if (cartFooter) cartFooter.classList.remove("hidden");

  // Check if we're on desktop and count unique items
  const isDesktop = window.innerWidth >= 768; // lg breakpoint
  const uniqueItemCount = cart.items.filter((item: CartItem) => item.quantity > 0).length;
  const shouldMoveToFooter = isDesktop && uniqueItemCount <= 3;

  // Always remove the section first to recreate it fresh
  if (shippingSection) {
    shippingSection.remove();
  }

  // Create a new shipping section
  shippingSection = document.createElement("div");
  shippingSection.id = "cartShippingSection";
  shippingSection.className = "shipping-container w-full mt-5 md:mt-0";

  // Build options dynamically based on available markets
  const markets = (window as any).ekMarkets || { DE: true, AT: true, CH: true, EU: true };
  const marketOptions = [
    { value: "DE", label: "Versand nach DE" },
    { value: "AT", label: "Versand nach AT" },
    { value: "CH", label: "Versand nach CH" },
    { value: "EU", label: "Versand nach EU" },
  ];
  const optionsHtml = marketOptions
    .filter((opt) => markets[opt.value])
    .map((opt) => `<option value="${opt.value}">${opt.label}</option>`)
    .join("\n              ");

  const isCH = ((window as any)?.Shopify?.country || latestMarketCountry || "").toUpperCase() === "CH";
  const chDutiesNotice = isCH
    ? `<div class="flex flex-nowrap items-center gap-2 justify-between w-full pt-2">
          <span class="shrink-0 bg-forestgreen text-white font-medium text-[14px] leading-none py-1 px-[10.5px] rounded-full whitespace-nowrap">Inkl. Zoll &amp; Steuern</span>
          <span class="text-sm whitespace-nowrap text-[#74A19C]">keine weiteren Zusatzkosten</span>
        </div>`
    : "";

  // Create the HTML for the shipping section
  shippingSection.innerHTML = `
      <div class="flex flex-col gap-2 mt-5">
        <div id="subtotalRow" class="flex flex-row justify-between items-center" style="display: none;">
          <span class="text-xs uppercase tracking-[0.6px] text-black/30">Zwischensumme</span>
          <span class="text-base tracking-[0.5px] text-secondary" id="subtotalPrice"></span>
        </div>
        <div class="flex flex-row justify-between items-center gap-5">
          <div>
            <select
              id="shippingSelect"
              class="py-1 pr-[20px] !pl-0 leading-none text-sm bg-white text-secondary border-none w-[133px] cartShippingCountrySelectArrow focus:outline-none focus:border-outline focus:ring-transparent"
            >
              ${optionsHtml}
            </select>
          </div>
          <div id="shippingPrice" class="text-black/30 text-base tracking-[0.5px]">Versandkostenfrei</div>
        </div>
        ${chDutiesNotice}
      </div>
      <div id="totalSumholder" class="mb-2 md:mb-3 md:mt-3 pt-3 flex flex-row justify-between items-start">
        <span class="text-xs uppercase tracking-[0.6px] mt-[14px] font-medium">Gesamtsumme</span>
        <div class="flex flex-col items-end">
          <span class="uppercase heading-3" id="totalCartPrice"></span>
          <span class="text-sm tracking-[0.6px] text-black/30">inkl. MwSt.</span>
        </div>
      </div>
    `;

  // Position the shipping section based on conditions
  if (shouldMoveToFooter) {
    // Add to cartFooter as first child
    if (cartFooter && cartFooter.firstChild) {
      cartFooter.insertBefore(shippingSection, cartFooter.firstChild);
    } else if (cartFooter) {
      cartFooter.appendChild(shippingSection);
    }
  } else {
    // Add to cartContents
    if (cartContents) {
      cartContents.appendChild(shippingSection);
    }
  }

  // Initialize shipping select after the section is in the DOM
  setTimeout(() => {
    const shippingSelect = document.querySelector("#cartShippingSection #shippingSelect") as HTMLSelectElement | null;
    if (!shippingSelect) return;

    // Debounced shipping update to prevent rate limiting
    const debouncedUpdateShipping = (country: string, cartData?: Cart) => {
      if (shippingUpdateDebounceTimer) {
        clearTimeout(shippingUpdateDebounceTimer);
      }
      shippingUpdateDebounceTimer = setTimeout(() => {
        shippingUpdateDebounceTimer = null;
        updateShippingCountry(country, cartData);
      }, 100);
    };

    const applyCountry = (country: string, cartData?: Cart) => {
      const group = mapCountryToGroup(country);
      if (!group || !SHIPPING_RATES[group]) return;
      shippingSelect.value = group;
      debouncedUpdateShipping(group, cartData);
    };

    // Apply initial country with cart data we already have (no extra fetch needed)
    const initialCountry = latestMarketCountry || (window as any)?.Shopify?.country || "";
    applyCountry(initialCountry.toUpperCase(), cart);

    // Only attach global event listeners once to prevent accumulation
    if (!shippingListenersAttached) {
      shippingListenersAttached = true;

      window.addEventListener("ek:market-ready", (e: any) => {
        const raw = (e && e.detail && e.detail.country) || getDetectedCountry();
        const select = document.querySelector("#cartShippingSection #shippingSelect") as HTMLSelectElement | null;
        if (select) {
          const group = mapCountryToGroup((raw || "").toUpperCase());
          if (group && SHIPPING_RATES[group]) {
            select.value = group;
            debouncedUpdateShipping(group);
          }
        }
      });

      window.addEventListener("ek:market-change", (e: any) => {
        const raw = e && e.detail && e.detail.country;
        if (raw) {
          const select = document.querySelector("#cartShippingSection #shippingSelect") as HTMLSelectElement | null;
          if (select) {
            const group = mapCountryToGroup((raw || "").toUpperCase());
            if (group && SHIPPING_RATES[group]) {
              select.value = group;
              debouncedUpdateShipping(group);
            }
          }
        }
      });
    }

    shippingSelect.addEventListener("change", (e) => {
      const value = (e.target as HTMLSelectElement).value;
      emit("ek:shipping-change", { value, source: "drawer" });
      debouncedUpdateShipping(value);
    });
  }, 50);

  // Calculate and update the total price
  calculateTotalPrice(cart);
}

/**
 * Updates the quantity of a cart item or removes it entirely
 *
 * This function:
 * 1. Fetches the current cart to get the current quantity of the item
 * 2. Calculates the new quantity based on the action (increase, decrease, remove)
 * 3. Makes a request to Shopify's Cart API to update the item quantity
 * 4. Updates the cart contents and count displays after the change
 *
 * @param itemId - The ID of the cart item to update
 * @param action - The action to perform: "increase", "decrease", or "remove"
 */

/**
 * Updates the cart shadows based on scroll position
 * Shows shadows at the top and/or bottom depending on scroll position
 */
function updateCartShadows() {
  // Use throttling to avoid excessive function calls during scrolling
  if (updateCartShadows.timeout) {
    clearTimeout(updateCartShadows.timeout);
  }

  updateCartShadows.timeout = setTimeout(() => {
    updateCartShadows.timeout = null;

    if (!cartContents || !cartFooter) return;

    const scrollTop = cartContents.scrollTop;
    const scrollHeight = cartContents.scrollHeight;
    const clientHeight = cartContents.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    // Add necessary CSS if it doesn't exist
    ensureShadowStyles();

    // Remove all shadow classes first
    cartFooter.classList.remove("cartDrawerShadow");
    cartContents.classList.remove("cartDrawerTopShadow");

    // Apply shadows based on scroll position
    if (maxScroll > 5) {
      // Only apply shadows if content is scrollable
      if (scrollTop < 5) {
        // At the top, can only scroll down
        cartFooter.classList.add("cartDrawerShadow");
        if (cartHeader) cartHeader.classList.remove("cartDrawerTopShadow");
      } else if (scrollTop >= maxScroll - 5) {
        // At the bottom, can only scroll up
        cartFooter.classList.remove("cartDrawerShadow");
        if (cartHeader) cartHeader.classList.add("cartDrawerTopShadow");
      } else {
        // In the middle, can scroll both ways
        cartFooter.classList.add("cartDrawerShadow");
        if (cartHeader) cartHeader.classList.add("cartDrawerTopShadow");
      }
    } else {
      // Content is not scrollable, remove all shadows
      cartFooter.classList.remove("cartDrawerShadow");
      if (cartHeader) cartHeader.classList.remove("cartDrawerTopShadow");
    }
  }, 0); // 100ms throttle
}

// Add timeout property to the function
updateCartShadows.timeout = null as any;

/**
 * Creates a brief tap animation for buttons to provide micro-interaction feedback
 * Only triggers on touch devices to replace persistent hover states
 */
function addTapAnimation(button: Element, event: Event) {
  // Only add tap animation on touch devices or if it's a touch event
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isTouchEvent = event.type === "touchstart" || event.type === "touchend";

  if (isTouchDevice || isTouchEvent) {
    // Add flash animation class
    button.classList.add("bg-black/10");

    // Remove the animation class after a brief moment
    setTimeout(() => {
      button.classList.remove("bg-black/10");
    }, 150);
  }
}

/**
 * Animates a cart item out of view before the network roundtrip completes.
 */
function animateCartItemOptimisticRemoval(cartItemRow: HTMLElement | null) {
  if (!cartItemRow || cartItemRow.dataset.removing === "true") return;
  cartItemRow.dataset.removing = "true";

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) {
    cartItemRow.remove();
    updateCartShadows();
    return;
  }

  const computedStyle = window.getComputedStyle(cartItemRow);
  const currentHeight = cartItemRow.offsetHeight;

  cartItemRow.style.height = `${currentHeight}px`;
  cartItemRow.style.marginBottom = computedStyle.marginBottom;
  cartItemRow.style.paddingTop = computedStyle.paddingTop;
  cartItemRow.style.paddingBottom = computedStyle.paddingBottom;
  cartItemRow.style.borderBottomWidth = computedStyle.borderBottomWidth;
  cartItemRow.style.overflow = "hidden";
  cartItemRow.style.pointerEvents = "none";
  cartItemRow.style.willChange = "opacity, transform, height, margin, padding";
  cartItemRow.style.transform = "translateX(0)";
  cartItemRow.style.opacity = "1";

  requestAnimationFrame(() => {
    cartItemRow.style.transition = "opacity 200ms ease, transform 240ms ease, height 280ms ease, margin 280ms ease, padding 280ms ease, border-width 280ms ease";
    cartItemRow.style.opacity = "0";
    cartItemRow.style.height = "0px";
    cartItemRow.style.marginBottom = "0px";
    cartItemRow.style.paddingTop = "0px";
    cartItemRow.style.paddingBottom = "0px";
    cartItemRow.style.borderBottomWidth = "0px";
  });

  const timeoutId = window.setTimeout(() => {
    pendingRemovalAnimationTimeouts.delete(timeoutId);
    // Guard against acting on a stale or already-detached element
    if (!cartItemRow || !cartItemRow.isConnected) {
      return;
    }
    cartItemRow.remove();
    updateCartShadows();
  }, CART_ITEM_REMOVE_ANIMATION_DURATION_MS);
  pendingRemovalAnimationTimeouts.add(timeoutId);
}

/**
   // Ensures the necessary shadow styles are added to the document
  */
function ensureShadowStyles() {
  const styleId = "cartDrawerShadowStyles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
       .cartDrawerShadow {
         -webkit-box-shadow: 0px -9px 6px -1px rgba(0, 0, 0, 0.05);
         box-shadow: 0px -9px 6px -1px rgba(0, 0, 0, 0.05);
         position: relative;
         z-index: 25;
       }
       .cartDrawerTopShadow {
         -webkit-box-shadow: 0px 9px 6px -1px rgba(0, 0, 0, 0.05);
         box-shadow: 0px 9px 6px -1px rgba(0, 0, 0, 0.05);
         position: relative;
         z-index: 15;
       }
       /* Only apply hover styles on devices that support hover */
       @media (hover: hover) and (pointer: fine) {
         .cart-item-decrease:hover:not(.disabled),
         .cart-item-increase:hover {
           background-color: rgba(0, 0, 0, 0.05);
         }
       }
     `;
    document.head.appendChild(style);
  }
}

/**
 * AbortController for the in-flight cart update. A new click for a *different*
 * item/action aborts the previous request. Same button clicked again (e.g.
 * double-click) does not abort, so the in-flight request can finish.
 */
let cartUpdateController: AbortController | null = null;
let cartUpdateInFlight: { itemId: string; action: string } | null = null;

async function performCartUpdate(itemId: string, action: "increase" | "decrease" | "remove", signal: AbortSignal): Promise<void> {
  let newQuantity = 0;

  if (action !== "remove") {
    const cartResponse = await fetch("/cart.js", { signal });
    const cart: Cart = await cartResponse.json();

    if (signal.aborted) return;

    const item = cart.items.find((item: CartItem) => item.id.toString() === itemId);
    if (!item) {
      fetchCartItems();
      return;
    }

    newQuantity = item.quantity;
    if (action === "increase") {
      newQuantity += 1;
    } else if (action === "decrease") {
      // If quantity is 1, don't decrease further
      if (item.quantity > 1) {
        newQuantity -= 1;
      } else {
        // Don't update the cart if quantity is already 1
        updateDecreaseButtonState(cart);
        return;
      }
    }
  }

  if (signal.aborted) return;

  const updateResponse = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: itemId, quantity: newQuantity }),
    signal,
  });
  if (!updateResponse.ok) {
    throw new Error(`Cart update failed with status ${updateResponse.status}`);
  }
  const updatedCart: Cart = await updateResponse.json();

  if (signal.aborted) return;

  // Update active currency from cart data
  activeCurrency = (updatedCart as any)?.currency || activeCurrency;
  // Fetch compare_at_prices from Product API (uses cache for efficiency)
  const compareAtPrices = await fetchVariantCompareAtPrices(updatedCart);

  if (signal.aborted) return;

  updateCartContents(updatedCart, compareAtPrices);
  updateCartCount(updatedCart.item_count);
  updateCartShadows();
  calculateTotalPrice(updatedCart);
  updateDecreaseButtonState(updatedCart);
}

function updateItemQuantity(itemId: string | undefined, action: "increase" | "decrease" | "remove") {
  if (!itemId) return;

  const sameAsInFlight = cartUpdateInFlight?.itemId === itemId && cartUpdateInFlight?.action === action;
  if (sameAsInFlight) return;

  if (cartUpdateController) {
    cartUpdateController.abort();
  }
  cartUpdateController = new AbortController();
  cartUpdateInFlight = { itemId, action };
  const signal = cartUpdateController.signal;

  performCartUpdate(itemId, action, signal)
    .catch((error) => {
      if (error?.name === "AbortError") return;
      console.error("Error updating cart:", error);
      fetchCartItems();
    })
    .finally(() => {
      if (cartUpdateInFlight?.itemId === itemId && cartUpdateInFlight?.action === action) {
        cartUpdateInFlight = null;
      }
    });
}

/**
 * Updates the cart count display in the UI
 *
 * This function:
 * 1. Updates the count number in the cart icon
 * 2. Shows a highlight animation to draw attention to the change
 * 3. Hides the highlight after a delay
 *
 * @param count - The new cart item count to display
 */

function updateCartCount(count: number) {
  // Update all cart count elements (mobile and desktop)
  const cartCountElements = document.querySelectorAll(".cart-count");

  cartCountElements.forEach((cartCountWrapper) => {
    // Update the number display
    const numberElement = cartCountWrapper.querySelector(".number");
    if (numberElement) {
      numberElement.textContent = count.toString();
    }

    // Show/hide highlight animation (only exists on mobile)
    const highlightElement = cartCountWrapper.querySelector(".highlight");
    if (highlightElement) {
      highlightElement.classList.remove("hidden");
      setTimeout(() => {
        highlightElement.classList.add("hidden");
      }, 1000);
    }

    // Show or hide the cart count badge
    if (count > 0) {
      cartCountWrapper.classList.remove("hidden");
    } else {
      cartCountWrapper.classList.add("hidden");
    }
  });
}

// Helper function to update decrease button states based on item quantities
function updateDecreaseButtonState(cart: Cart) {
  if (!cart || !cart.items) return;

  document.querySelectorAll(".cart-item-decrease").forEach((button) => {
    const itemId = (button as HTMLElement).dataset.id;
    if (!itemId) return;

    const item = cart.items.find((item: any) => item.id.toString() === itemId);
    if (!item) return;

    if (item.quantity <= 1) {
      button.classList.add("disabled", "text-black/30");
      (button as HTMLButtonElement).disabled = true;
    } else {
      button.classList.remove("disabled", "text-black/30");
      (button as HTMLButtonElement).disabled = false;
    }
  });
}

// Event listeners for all cart icons
document.querySelectorAll(".cart-icon, #cartIconNav").forEach((cartIcon) => {
  cartIcon.addEventListener("click", openCartDrawer);
});

// Close cart when clicking on the down arrow (which is in the header)
if (cartOverlay) {
  const closeCartArrow = cartOverlay.querySelector(".icon-hover-bubble");
  const desktopArrowBack = cartOverlay.querySelector("#desktopArrowBack");
  if (closeCartArrow) {
    closeCartArrow.addEventListener("click", closeCartDrawer);
  }
  if (desktopArrowBack) {
    desktopArrowBack.addEventListener("click", closeCartDrawer);
  }
}

// Add event delegation for the cartOverlay to only close when clicking the background
if (cartOverlay) {
  cartOverlay.addEventListener("click", (event) => {
    // Only close if clicking directly on the overlay (not its children)
    if (event.target === cartOverlay) {
      closeCartDrawer();
    }
  });
}

// Close cart when pressing Escape key, unless the upsell drawer is still in front
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const upsellOverlay = document.querySelector("[data-upsell-overlay]:not(.hidden)");
  const cartIsVisible = Boolean(cartOverlay && !cartOverlay.classList.contains("hidden"));
  if (upsellOverlay && !cartIsVisible) return;
  closeCartDrawer();
});

// Update cart count display when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  fetchCartItems();

  // Handle window resize which might affect scrollability and shipping section placement
  window.addEventListener(
    "resize",
    () => {
      updateCartShadows();
      if (!lastCartSnapshot) return;
      updateShippingSection(lastCartSnapshot);
      calculateTotalPrice(lastCartSnapshot);
    },
    { passive: true },
  );

  // Initial shadow check after content is loaded
  setTimeout(updateCartShadows, 300);

  // Clean up when page unloads
  window.addEventListener("beforeunload", () => {
    if (updateCartShadows.timeout) {
      clearTimeout(updateCartShadows.timeout);
    }
    if (shippingUpdateDebounceTimer) {
      clearTimeout(shippingUpdateDebounceTimer);
    }
  });
});

/**
 * Globally accessible function to open the cart drawer from anywhere in the application
 *
 * This function is exported and made available on the window object to allow other
 * scripts and components to open the cart drawer without direct access to the
 * internal cartDrawer implementation.
 *
 * @param event - Optional event object that will be prevented if provided
 */
export function openCartDrawerGlobal(event?: Event) {
  if (globalOpenCartDrawer) {
    globalOpenCartDrawer(event);
  }
}

export function openCartDrawerFromUpsell() {
  openFromUpsell = true;
  openCartDrawer();
}

/**
 * Re-renders drawer contents, count and totals from the server cart without opening the drawer
 */
export async function refreshCartDrawer() {
  await fetchCartItems();
}

export async function syncCartCountFromServer() {
  try {
    const response = await fetch("/cart.js");
    if (!response.ok) return;
    const cart = (await response.json()) as Cart;
    updateCartCount(cart.item_count);
  } catch {
    // Cart badge can stay stale; the next drawer open refreshes it.
  }
}
